/**
 * Aesthetic Skincare: motion and interaction for the ohmy-* sections.
 * Scroll reveals, split headlines, scroll-driven text, parallax, counters,
 * the draggable treatment rail, the before/after slider, image-switching rows
 * and the treatment menu's category navigation.
 */
(function () {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const hasObserver = 'IntersectionObserver' in window;
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

  /* ----- before/after slider ----- */
  if (!customElements.get('ohmy-compare')) {
    customElements.define(
      'ohmy-compare',
      class OhmyCompare extends HTMLElement {
        connectedCallback() {
          this.handle = this.querySelector('.ohmy-compare__handle');
          this.interacted = false;
          this.setPos(parseFloat(this.dataset.start || '50'));

          this.addEventListener('pointerdown', (event) => this.onPointerDown(event));
          this.addEventListener('pointermove', (event) => this.onPointerMove(event));
          this.addEventListener('pointerup', () => this.classList.remove('is-dragging'));
          this.addEventListener('pointercancel', () => this.classList.remove('is-dragging'));
          if (this.handle) this.handle.addEventListener('keydown', (event) => this.onKeyDown(event));

          // Invite interaction: sweep across once the slider scrolls into view
          if (!reduceMotion && hasObserver) {
            const seen = new IntersectionObserver(
              (entries) => {
                if (!entries.some((entry) => entry.isIntersecting)) return;
                seen.disconnect();
                window.setTimeout(() => this.nudge(), 350);
              },
              { threshold: 0.45 }
            );
            seen.observe(this);
          } else {
            this.setPos(parseFloat(this.dataset.rest || '50'));
          }
        }

        setPos(value) {
          this.pos = clamp(value, 0, 100);
          this.style.setProperty('--pos', `${this.pos}%`);
          this.classList.toggle('is-full-before', this.pos > 92);
          this.classList.toggle('is-full-after', this.pos < 8);
          if (this.handle) this.handle.setAttribute('aria-valuenow', String(Math.round(this.pos)));
        }

        posFromEvent(event) {
          const rect = this.getBoundingClientRect();
          return ((event.clientX - rect.left) / rect.width) * 100;
        }

        onPointerDown(event) {
          this.interacted = true;
          if (this.raf) cancelAnimationFrame(this.raf);
          this.classList.add('is-dragging');
          this.setPointerCapture(event.pointerId);
          this.setPos(this.posFromEvent(event));
        }

        onPointerMove(event) {
          if (!this.classList.contains('is-dragging')) return;
          this.setPos(this.posFromEvent(event));
        }

        onKeyDown(event) {
          const step = event.shiftKey ? 10 : 4;
          const moves = { ArrowLeft: -step, ArrowDown: -step, ArrowRight: step, ArrowUp: step };
          if (event.key in moves) this.setPos(this.pos + moves[event.key]);
          else if (event.key === 'Home') this.setPos(0);
          else if (event.key === 'End') this.setPos(100);
          else return;
          this.interacted = true;
          event.preventDefault();
        }

        /* Glide from the starting position past the resting point, then settle back */
        nudge() {
          if (this.interacted) return;
          const from = this.pos;
          const to = parseFloat(this.dataset.rest || '50');
          const overshoot = clamp(to - (from - to) * 0.35, 4, 96);
          const duration = 2000;
          let start = null;
          const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

          const frame = (now) => {
            if (this.interacted) return;
            if (start === null) start = now;
            const t = Math.min(1, (now - start) / duration);
            const e = ease(t);
            const value = e < 0.62 ? from + (overshoot - from) * (e / 0.62) : overshoot + (to - overshoot) * ((e - 0.62) / 0.38);
            this.setPos(value);
            if (t < 1) this.raf = requestAnimationFrame(frame);
          };
          this.raf = requestAnimationFrame(frame);
        }
      }
    );
  }

  /* ----- scroll reveals ----- */
  function initReveals(root) {
    const targets = root.querySelectorAll('[data-ohmy-reveal]:not(.is-in)');
    if (!targets.length) return;

    if (reduceMotion || !hasObserver) {
      targets.forEach((el) => el.classList.add('is-in'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    targets.forEach((el) => observer.observe(el));
  }

  /* ----- wrap each word of an element in spans, keeping inline markup such as <em> ----- */
  function wrapWords(element, wrap) {
    let index = 0;
    const walk = (node) => {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          const fragment = document.createDocumentFragment();
          child.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) fragment.appendChild(document.createTextNode(' '));
            else fragment.appendChild(wrap(part, index++));
          });
          node.replaceChild(fragment, child);
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          walk(child);
        }
      });
    };
    walk(element);
    return index;
  }

  /* Headlines whose words rise into place */
  function initSplit(root) {
    root.querySelectorAll('[data-ohmy-split]:not(.is-split)').forEach((element) => {
      if (!reduceMotion) {
        element.setAttribute('aria-label', element.textContent.trim());
        wrapWords(element, (word, index) => {
          const outer = document.createElement('span');
          outer.className = 'ohmy-w';
          outer.setAttribute('aria-hidden', 'true');
          outer.style.setProperty('--ohmy-w', String(index));
          const inner = document.createElement('span');
          inner.textContent = word;
          outer.appendChild(inner);
          return outer;
        });
      }
      element.classList.add('is-split');
    });
  }

  /* Paragraphs whose words darken as they are scrolled through */
  const wordBlocks = [];
  function initWords(root) {
    if (reduceMotion) return;
    root.querySelectorAll('[data-ohmy-words]:not(.is-ready)').forEach((element) => {
      wrapWords(element, (word) => {
        const span = document.createElement('span');
        span.className = 'ohmy-word';
        span.textContent = word;
        return span;
      });
      element.classList.add('is-ready');
      wordBlocks.push({ element, words: Array.from(element.querySelectorAll('.ohmy-word')) });
    });
  }

  /* ----- counters ----- */
  function initCounters(root) {
    const counters = root.querySelectorAll('[data-ohmy-count]:not(.is-counted)');
    if (!counters.length) return;

    const run = (el) => {
      el.classList.add('is-counted');
      const target = parseFloat(el.dataset.ohmyCount);
      const decimals = (el.dataset.ohmyCount.split('.')[1] || '').length;
      if (reduceMotion || Number.isNaN(target)) return;
      const duration = 1400;
      let start = null;
      const frame = (now) => {
        if (start === null) start = now;
        const t = Math.min(1, (now - start) / duration);
        el.textContent = (target * (1 - Math.pow(1 - t, 3))).toFixed(decimals);
        if (t < 1) requestAnimationFrame(frame);
      };
      requestAnimationFrame(frame);
    };

    if (!hasObserver) return counters.forEach(run);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          run(entry.target);
        });
      },
      { threshold: 0.6 }
    );
    counters.forEach((el) => observer.observe(el));
  }

  /* ----- scroll-linked effects (parallax + word highlighting) ----- */
  let parallaxItems = [];
  let ticking = false;

  function updateScrollEffects() {
    ticking = false;
    const viewport = window.innerHeight;

    parallaxItems.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.bottom < -100 || rect.top > viewport + 100) return;
      const offset = (rect.top + rect.height / 2 - viewport / 2) / viewport;
      const strength = parseFloat(el.dataset.ohmyParallax) || 40;
      el.style.setProperty('--ohmy-py', `${(-offset * strength).toFixed(1)}px`);
    });

    wordBlocks.forEach(({ element, words }) => {
      const rect = element.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > viewport) return;
      const progress = clamp((viewport * 0.86 - rect.top) / (viewport * 0.5 + rect.height), 0, 1);
      const lit = Math.round(progress * words.length);
      words.forEach((word, index) => word.classList.toggle('is-on', index < lit));
    });
  }

  function requestScrollUpdate() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(updateScrollEffects);
  }

  function initParallax(root) {
    if (reduceMotion) return;
    root.querySelectorAll('[data-ohmy-parallax]').forEach((el) => {
      if (!parallaxItems.includes(el)) parallaxItems.push(el);
    });
    parallaxItems = parallaxItems.filter((el) => el.isConnected);
  }

  /* ----- horizontal rail: arrows and drag-to-scroll ----- */
  function initRails(root) {
    root.querySelectorAll('[data-ohmy-rail]:not(.is-ready)').forEach((rail) => {
      rail.classList.add('is-ready');
      const section = rail.closest('.ohmy-section') || document;
      const step = () => {
        const card = rail.firstElementChild;
        return card ? card.getBoundingClientRect().width + parseFloat(getComputedStyle(rail).columnGap || '0') : 320;
      };

      section.querySelectorAll('[data-ohmy-rail-prev], [data-ohmy-rail-next]').forEach((button) => {
        const direction = button.hasAttribute('data-ohmy-rail-next') ? 1 : -1;
        button.addEventListener('click', () => {
          rail.scrollBy({ left: direction * step(), behavior: reduceMotion ? 'auto' : 'smooth' });
        });
      });

      let startX = 0;
      let startScroll = 0;
      let dragging = false;
      let moved = false;

      rail.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        dragging = true;
        moved = false;
        startX = event.clientX;
        startScroll = rail.scrollLeft;
      });

      window.addEventListener('pointermove', (event) => {
        if (!dragging) return;
        const delta = event.clientX - startX;
        if (!moved && Math.abs(delta) > 6) {
          moved = true;
          rail.classList.add('is-dragging');
        }
        if (moved) rail.scrollLeft = startScroll - delta;
      });

      const stop = () => {
        if (!dragging) return;
        dragging = false;
        // Keep links inert until the click that ends the drag has passed
        window.setTimeout(() => rail.classList.remove('is-dragging'), 0);
      };
      window.addEventListener('pointerup', stop);
      window.addEventListener('pointercancel', stop);
      rail.addEventListener('dragstart', (event) => event.preventDefault());
    });
  }

  /* ----- rows that switch an image ----- */
  function initSwitchers(root) {
    root.querySelectorAll('[data-ohmy-switcher]:not(.is-ready)').forEach((switcher) => {
      switcher.classList.add('is-ready');
      const images = Array.from(switcher.querySelectorAll('[data-ohmy-switch-image]'));
      const triggers = Array.from(switcher.querySelectorAll('[data-ohmy-switch]'));

      const activate = (key) => {
        images.forEach((image) => image.classList.toggle('is-active', image.dataset.ohmySwitchImage === key));
        triggers.forEach((trigger) => trigger.classList.toggle('is-active', trigger.dataset.ohmySwitch === key));
      };

      triggers.forEach((trigger) => {
        trigger.addEventListener('mouseenter', () => activate(trigger.dataset.ohmySwitch));
        trigger.addEventListener('focus', () => activate(trigger.dataset.ohmySwitch));
      });
    });
  }

  /* ----- treatment menu category navigation ----- */
  function initMenuNav(root) {
    root.querySelectorAll('[data-ohmy-menu]:not(.is-ready)').forEach((menu) => {
      menu.classList.add('is-ready');
      const links = Array.from(menu.querySelectorAll('[data-ohmy-menu-link]'));
      const groups = links.map((link) => document.getElementById(link.hash.slice(1))).filter((group) => group !== null);
      if (!links.length || !groups.length || !hasObserver) return;

      const setActive = (id) => {
        links.forEach((link) => {
          const active = link.hash === `#${id}`;
          link.classList.toggle('is-active', active);
          if (active) link.setAttribute('aria-current', 'true');
          else link.removeAttribute('aria-current');
        });
      };

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) setActive(entry.target.id);
          });
        },
        { rootMargin: '-35% 0px -55% 0px' }
      );

      groups.forEach((group) => observer.observe(group));
    });
  }

  /* ----- background videos respect reduced motion ----- */
  function initVideos(root) {
    if (!reduceMotion) return;
    root.querySelectorAll('video[data-ohmy-video]').forEach((video) => {
      video.removeAttribute('autoplay');
      video.pause();
    });
  }

  function init(root) {
    initSplit(root);
    initWords(root);
    initReveals(root);
    initCounters(root);
    initParallax(root);
    initRails(root);
    initSwitchers(root);
    initMenuNav(root);
    initVideos(root);
    requestScrollUpdate();
  }

  // Horizon scrolls inside .page-wrapper on desktop, so listen in the capture phase
  document.addEventListener('scroll', requestScrollUpdate, { capture: true, passive: true });
  window.addEventListener('resize', requestScrollUpdate, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  // Sections re-rendered in the theme editor need their behaviour re-run
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  /* ----- hide product cards whose main image fails to load ----- */
  // Liquid already skips products with no image; this catches files that exist but won't load.
  const PRODUCT_CARD =
    '.product-grid__item, .resource-list__item, .predictive-search-results__card--product, .ohmy-product';

  const hideIfBroken = (img) => {
    const card = img.closest(PRODUCT_CARD);
    // Only the card's main image counts; a broken hover image shouldn't hide the product
    if (card && card.querySelector('img') === img) card.style.display = 'none';
  };

  // Image errors don't bubble, so listen in the capture phase
  document.addEventListener(
    'error',
    (event) => {
      if (event.target instanceof HTMLImageElement) hideIfBroken(event.target);
    },
    true
  );

  // This script is deferred, so catch images that already failed before it ran
  document.querySelectorAll('img').forEach((img) => {
    if (img.complete && img.naturalWidth === 0 && img.currentSrc) hideIfBroken(img);
  });

  window.OhmyReady = true;
})();
